import { LitElement, html, css, nothing, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyageShoreContextModel } from '../../../src/models/voyage-shore-context-model';
import { UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { FilterUtils } from '../../voy-global-filters/src/filter-utils';
import { ShoreContextEvent } from '../../../src/models/types/voyage-shore-context-model.types';
import { VoyToggleButton } from '../../voy-toggle-button/src/voy-toggle-button';
import '../../voy-toggle-button/src/voy-toggle-button';
import "@maersk-global/mds-components-core/mc-switch";

@customElement('voyage-drawer-panel')
export class VoyageDrawerPanel extends LitElement {

    @property({ type: String }) drawerTitle: string = "";
    @property({ type: String }) drawerDesc?: string = ""
    @property({ type: Boolean, reflect: true }) shown: boolean = false;
    @property({ type: Boolean, reflect: true }) showMapViewOptions: boolean = false;
    @property({ type: Boolean, reflect: true }) transparent: boolean = false;
    @property({ type: Boolean, reflect: true }) showCloseBtn: boolean = false;
    @property({ type: Boolean, reflect: true }) showOnTop: boolean = false;
    @property({ type: Boolean, reflect: true }) numberOfVessels?: number = 0; 
    @property({ attribute: false }) filterResult?: string;
    @property({ attribute: false }) public timeLineVisible: boolean = false;
    @property({ type: Boolean, reflect: true }) showToggler: boolean = false;
    @property({ type: String }) togglerLabel: string = "";
    @property({ type: String }) fit: string = "";
    @property({ type: Boolean, reflect: true }) checked: boolean = false;
    @property({ attribute: false }) triggered:boolean = false;
    
    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("triggered")) {
                this._removeFilters()
        }
    }

    
    constructor() {
        super();

        this._close = this._close.bind(this)
        this._toggleTimeLine = this._toggleTimeLine.bind(this)
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
    }
    static get styles() {
        return [
            css`
                :host {
                    display: block;
                    position: fixed;
                    box-sizing: border-box;
                    transform: translateX(100%);
                    background: white;
                    transition: transform 150ms ease-out;
                    border: 1px solid var(--functional-grey-400, grey);
                    box-shadow: 2px 2px 8px var(--secondary-voyage-box-shadow);
                    border-radius: 4px 4px 4px 4px;
                    top: 112px; /* HACK: change layout so this is absolute positioned with map, which should start under the topbar */
                    right: 0px;
                    margin-left: 10px;
                }

                :host([shown]) {
                    transform: translateX(0);
                    right: 16px;
                }

                :host([showWeather]) {
                    width: 340px !important;
                }

                :host([showMapViewOptions]) {
                    transform: translateX(0);
                    right: 216px;
                }

                :host([transparent]) {
                    opacity: 0.5;
                }

                :host(:hover) {
                    opacity: 1;
                }

                #topbar {
                    width: 100%;
                    padding: 16px;
                    touch-action: none;
                    display: grid;
                    grid-template-columns: 1fr auto;
                    align-items: center;
                    border-bottom: 1px solid var(--functional-grey-600, grey);
                    box-sizing: border-box;
                    user-select: none;
                }

                .drawerTitle {
                    grid-column: 1;
                    font-family: var(--component-paragraph-ff, 'serif');
                    font-size: 20px;
                }

                .close {
                    grid-column: 2;
                    background: none;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    padding: 12px 10px 10px;
                    fill: var(--neutrals-grey-02, black);
                }

                .close > svg {
                    display: block;
                    width: 13px;
                }

                .container {
                    padding: 16px;
                    overflow-y: auto;
                    max-height: calc(100vh - 243px);
                    padding-bottom: 0px;
                }

                .full-pannel {
                    max-height: calc(100vh - 187px);
                }
                .container[performance]{
                    padding: 0%
                }

                .remove-filters {
                    text-decoration: underline;
                    font-family: 'Maersk Text';
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 20px;
                    color: #0072AA;
                    cursor: pointer;
                    padding-left: 20px;
                }

                .save-filters {
                    line-height: 20px;
                    fill: #0072AA;
                    cursor: pointer;
                    padding-left: 10px;
                    vertical-align: bottom;
                    position:fixed;
                    right:20px;
                }

                .sub-text {
                    font-family: 'Maersk Text';
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 16px;
                    color: #878787;
                    padding-top: 8px;
                }
                .mt-5 {
                    margin-top: 5px;
                }
            `
        ]   
    };

    render() {
        let className = this.timeLineVisible ? "container" : "container full-pannel";
        
        if(this.drawerTitle === "Distance deviation"){
            className = "container"
        }

        return html`
        <div id="topbar">
            <div class="drawerTitle">${this.drawerTitle}
            ${this.drawerTitle === "Filters" ? html`<span class="remove-filters" @click=${() => this._removeFilters()}>Clear all (${ this.numberOfVessels} vessels)</span> `
             : nothing}  
        </div>
            ${this.showCloseBtn
                ? html`<button class="close" @pointerdown=${this._close}>${SvgIcons.lightTimes}</button>`
                : nothing}
             ${this.drawerDesc 
            ? html`<span class="sub-text">${this.drawerDesc}</span>` : nothing }
            
            ${this.drawerTitle === "Activity Log" ? html`<voy-toggle-button label="Need action"
                                   @input=${(evt: InputEvent) => this._handleNeedAction((<VoyToggleButton>evt.target).checked)}></voy-toggle-button>` : nothing}
            ${this.showToggler ? html `<mc-switch 
                label="${this.togglerLabel}"
                fit="${this.fit}"
                name="toggler"
                class="mt-5"
                ?checked="${this.checked}"
                @change=${this.onToggle}></mc-switch>` : this.drawerDesc 
                ? html`<span class="sub-text">${html`${this.drawerDesc}`}</span>` : nothing }
        </div>
        <div class=${className} ?performance=${this.drawerTitle=="Performance" }><slot></slot></div>
        `;
    }

    private onToggle(evt:Event){
        this.dispatchEvent(new Event('toggle'));
        evt.stopPropagation();
        return false;
    }

    private _close(evt:Event) {
        this.dispatchEvent(new Event('close'));
        evt.stopPropagation();
        return false;
    }

    private _handleNeedAction(isChecked : boolean) {
        this.dispatchEvent(new CustomEvent('need-action', {
            detail: {
                needAction: isChecked
            }
        }));
    }

    private _removeFilters(){
        this.filterResult = FilterUtils.filterToString({ commands:[], text: []});
        UiStateModel!.setValue(UiStateModelParam.FISubView);
        UiStateModel!.setValue('q', this.filterResult);

        this.dispatchEvent(new CustomEvent("filters-changed", {
            detail: {
                numberOfActiveFilters: 0,
                 numberOfVessels: 0
            }
        })) 
        VoyageShoreContextModel!.setClearFilters();
    }

    private _saveFilters(){
        let filters = UiStateModel!.getValue('q')
        this.dispatchEvent(new CustomEvent("save-filters", {
            detail: {
                filterResult: filters
            }
        }))
    }

    private _toggleTimeLine():void {
        this.timeLineVisible = !this.timeLineVisible
    }
}
